import { Suspense, lazy } from "react";
import { RouterProvider, createBrowserRouter } from "react-router-dom";

import clsx from "clsx";
import { InfinitySpin } from "react-loader-spinner";
import MainLayout from "./layout/MainLayout";
import ErrorPage from "./pages/feedback/ErrorPage";
import NotFound from "./pages/feedback/NotFound";
import ProtectedRoute from "./components/ProtectedRoute";

const Login = lazy(() => import("./pages/Login"));
const Dashboard = lazy(() => import("./pages/main/Dashboard"));

const User = lazy(() => import("./pages/main/Users/UserList"));
const CreateUser = lazy(() => import("./pages/main/Users/CreateUser"));
const EditUser = lazy(() => import("./pages/main/Users/EditUser"));

const Specs = lazy(() => import("./pages/main/VehicleSpecs/SpecsList"));
const SpecsView = lazy(() => import("./pages/main/VehicleSpecs/SpecsView"));
const UploadData = lazy(() => import("./pages/main/VehicleSpecs/UploadData"));
const SampleFile = lazy(() => import("./pages/main/SampleFile/SampleFile"));
const RetailPricePage = lazy(() => import("./pages/main/RetailPrice"));
const UploadRetailPriceFile = lazy(() => import("./pages/main/RetailPrice/Upload"));

const Audit = lazy(() => import("./pages/main/Audit/Audit"));
const AuditReport = lazy(() => import("./pages/main/AuditReport/AuditReport"));

const Rule = lazy(() => import("./pages/main/Rule/Rule"));

const Make = lazy(() => import("./pages/main/Make"));
const Model = lazy(() => import("./pages/main/Model"));
const AverageMileagePage = lazy(() => import("./pages/main/AverageMileage"));
const Category = lazy(() => import("./pages/main/Category"));
const MakeModelCategory = lazy(() => import("./pages/main/MakeModelCategory"));
const DepreciationBandPage = lazy(() => import("./pages/main/DepreciationBand"));

const EvaluationSummaryPage = lazy(() => import("./pages/main/EvaluationSummary"));

const PriceAnalysis = lazy(() => import("./pages/main/PriceAnalysis"));
const PriceAnalysisView = lazy(() => import("./pages/main/PriceAnalysis/View"));
const PriceAnalysisReport = lazy(() => import("./pages/main/PriceAnalysis/Report"));

const Settings = lazy(() => import("./pages/main/Settings"));
const DataBackups = lazy(() => import("./pages/main/Backup"));

const PageLoader = ({ fullScreen = false }) => (
  <div
    className={clsx(
      "flex h-full items-center justify-center",
      fullScreen && "h-screen",
    )}
  >
    <InfinitySpin visible={true} width="200" color="#0C71C3" />
  </div>
);

const router = createBrowserRouter(
  [
    {
      path: "/",
      element: <MainLayout />,
      errorElement: <ErrorPage />,
      children: [
        {
          index: true,
          element: (
            <Suspense fallback={<PageLoader />}>
              <Dashboard />
            </Suspense>
          ),
        },
        {
          path: "user",
          element: <ProtectedRoute requiredPermission="users" />,
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<PageLoader />}>
                  <User />
                </Suspense>
              ),
            },
            {
              path: "create",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <CreateUser />
                </Suspense>
              ),
            },
            {
              path: "edit/:id",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <EditUser />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "vehicle-specs",
          element: <ProtectedRoute requiredPermission="vehicle_specs" />,
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<PageLoader />}>
                  <Specs />
                </Suspense>
              ),
            },
            {
              path: "view/:id",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <SpecsView />
                </Suspense>
              ),
            },
            {
              path: "upload-data",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <UploadData />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "valuation-summary",
          element: <ProtectedRoute requiredPermission="valuation_summary" />,
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<PageLoader />}>
                  <EvaluationSummaryPage />
                </Suspense>
              ),
            },
          ],
        },

        {
          path: "average-mileage",
          element: <ProtectedRoute requiredPermission="average_mileage" />,
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<PageLoader />}>
                  <AverageMileagePage />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "upload-sample",
          element: <ProtectedRoute requiredPermission="sample_file" />,
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<PageLoader />}>
                  <SampleFile />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "retail-price",
          element: <ProtectedRoute requiredPermission="retail_price" />,
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<PageLoader />}>
                  <RetailPricePage />
                </Suspense>
              ),
            },
            {
              path: "upload-file",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <UploadRetailPriceFile />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "audit",
          element: <ProtectedRoute requiredPermission="audit" />,
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<PageLoader />}>
                  <Audit />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "audit-report",
          element: <ProtectedRoute requiredPermission="audit_report" />,
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<PageLoader />}>
                  <AuditReport />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "rule",
          element: <ProtectedRoute requiredPermission="rule" />,
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<PageLoader />}>
                  <Rule />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "make",
          element: <ProtectedRoute requiredPermission="make" />,
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<PageLoader />}>
                  <Make />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "model",
          element: <ProtectedRoute requiredPermission="model" />,
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<PageLoader />}>
                  <Model />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "category",
          element: <ProtectedRoute requiredPermission="category" />,
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<PageLoader />}>
                  <Category />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "make-model-category",
          element: <ProtectedRoute requiredPermission="make_model_category" />,
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<PageLoader />}>
                  <MakeModelCategory />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "time-depreciation",
          element: <ProtectedRoute requiredPermission="depreciation_band" />,
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<PageLoader />}>
                  <DepreciationBandPage />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "depreciation-band/:id?",
          element: <ProtectedRoute requiredPermission="depreciation_band" />,
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<PageLoader />}>
                  <DepreciationBandPage />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "price-analysis",
          element: <ProtectedRoute requiredPermission="price_analysis" />,
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PriceAnalysis />
                </Suspense>
              ),
            },
            {
              path: "view/:id",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PriceAnalysisView />
                </Suspense>
              ),
            },
            {
              path: "report",
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PriceAnalysisReport />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "data-backups",
          element: <ProtectedRoute requiredPermission="data_backup" />,
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<PageLoader />}>
                  <DataBackups />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: "settings",
          element: <ProtectedRoute requiredPermission="settings" />,
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<PageLoader />}>
                  <Settings />
                </Suspense>
              ),
            },
          ],
        },
      ],
    },
    {
      path: "/login",
      errorElement: <ErrorPage />,
      element: (
        <Suspense fallback={<PageLoader fullScreen />}>
          <Login />
        </Suspense>
      ),
    },
    // {
    //   path: "/session",
    //   errorElement: <ErrorPage />,
    //   element: (
    //     <Suspense fallback={<PageLoader fullScreen />}>
    //       {/* <Session /> */}
    //     </Suspense>
    //   ),
    // },
    {
      path: "*",
      element: <NotFound />,
    },
  ],
  {
    basename: "/",
  },
);

function App() {
  return <RouterProvider router={router} />;
}


export default App;
